import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  type Automation,
  type AutomationKind,
  type AutomationStatusFilter,
  useAutomations,
} from "../../api/automations";
import type { Me, TeamSummary } from "../../api/client";
import { useMe, useTeams } from "../../api/hooks";
import { useReminders } from "../../api/reminders";
import { useDebounced } from "../../components/admin/memory/bits";
import { AutomationPanel } from "../../components/automations/AutomationPanel";
import { KindIcon, OwnerChip, StatusPill } from "../../components/automations/bits";
import {
  headerLine,
  isOwnerFilter,
  lastRunLabel,
  postsTo,
  sublineOf,
  whenShort,
} from "../../components/automations/logic";
import { NewAutomation } from "../../components/automations/NewAutomation";
import { RemindersList } from "../../components/automations/Reminders";
import { PageHead } from "../../components/shell/Page";
import { Icon, TeamIcon } from "../../components/ui/primitives";
import { t } from "../../i18n";
import { NARROW_PANEL, useMedia } from "../../lib/useMedia";

type Search = {
  owner?: string;
  status?: AutomationStatusFilter;
  kind?: AutomationKind;
  q?: string;
  /** Those posting into this channel (the channel header's "N automations" pill). */
  channel?: string;
  /** The automation open in the panel, or "new" for the New form. */
  selected?: string;
  /** R9-A4: my reminders instead of the automations. */
  view?: "reminders";
};

const STATUSES = new Set(["active", "paused", "needs_attention"]);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/_app/automations")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    ...(isOwnerFilter(s.owner) ? { owner: s.owner } : {}),
    ...(typeof s.status === "string" && STATUSES.has(s.status)
      ? { status: s.status as AutomationStatusFilter }
      : {}),
    ...(s.kind === "schedule" || s.kind === "event" ? { kind: s.kind } : {}),
    ...(typeof s.q === "string" && s.q.trim() ? { q: s.q } : {}),
    ...(typeof s.channel === "string" && UUID_RE.test(s.channel) ? { channel: s.channel } : {}),
    ...(typeof s.selected === "string" && s.selected ? { selected: s.selected } : {}),
    ...(s.view === "reminders" ? { view: "reminders" as const } : {}),
  }),
  component: Automations,
});

/** The URL's search without empty values. */
function clean(s: Search): Search {
  const out: Search = {};
  if (s.owner) out.owner = s.owner;
  if (s.status) out.status = s.status;
  if (s.kind) out.kind = s.kind;
  if (s.q?.trim()) out.q = s.q;
  if (s.channel) out.channel = s.channel;
  if (s.selected) out.selected = s.selected;
  if (s.view) out.view = s.view;
  return out;
}

function Automations() {
  const me = useMe();
  if (!me.data) return null;
  return <AutomationsPage me={me.data} />;
}

/** The Automations page (PLAN §6.5 `/automations`, §8.8; mockup `section#automations`): the
 *  header with the month's spend, the toolbar (search, Timed / Triggered, owner chips, Needs
 *  attention), the list, and the open automation (or the New form) in the side panel. One chip
 *  at a time: each chip's count is what it shows. */
function AutomationsPage({ me }: { me: Me }) {
  const search = Route.useSearch();
  const { owner, status, kind, q, channel, selected, view } = search;
  const reminders = useReminders();
  const navigate = useNavigate();
  const narrow = useMedia(NARROW_PANEL);
  const list = useAutomations({ owner, status, kind, q, channel });
  const teams = useTeams();
  const teamById = useMemo(() => new Map((teams.data ?? []).map((t) => [t.id, t])), [teams.data]);
  const data = list.data;
  const items = data?.items ?? [];
  const counts = data?.counts;
  const filtered = Boolean(owner || status || kind || q || channel || view);
  const channelLabel =
    items.find((a) => a.destination.id === channel)?.destination.label ?? t("auto.theChannel");
  const none = Boolean(data && data.counts.all === 0 && !filtered);
  const fromList = useRef(false);

  const go = (next: Search, replace = false) =>
    void navigate({ to: "/automations", search: clean(next), replace });

  // The search box types into the URL a moment after the last key (Back and links set it too).
  const [text, setText] = useState(q ?? "");
  const typed = useDebounced(text.trim(), 250);
  const urlQ = useRef(q ?? "");
  useEffect(() => {
    if ((q ?? "") !== urlQ.current) {
      urlQ.current = q ?? "";
      setText(q ?? "");
    }
  }, [q]);
  useEffect(() => {
    if (typed === urlQ.current) return;
    urlQ.current = typed;
    // The entry under the open panel now has other filters: closing mustn't go Back to it.
    fromList.current = false;
    go({ ...search, q: typed || undefined }, true);
    // Only a new search moves the URL (go/search change with every navigation).
  }, [typed]);

  const open = (id: string) => {
    if (!selected) fromList.current = true;
    // Moving between automations replaces the entry: Back returns to the list.
    go({ ...search, selected: id }, Boolean(selected));
  };
  // Opened from the list: closing is the browser's Back. Opened by a link (or the filters
  // changed since): the list replaces the entry.
  const close = () => {
    if (fromList.current) {
      fromList.current = false;
      window.history.back();
    } else go({ ...search, selected: undefined }, true);
  };
  const closeRef = useRef(close);
  closeRef.current = close;
  // Escape closes the panel: not while a dialog (which handles its own) is open, nor from a
  // field (a draft isn't thrown away by a stray key).
  useEffect(() => {
    if (!selected) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      if (e.target instanceof Element && e.target.closest("input, textarea, select")) return;
      closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  // One chip at a time (its count is what it shows); pressing the pressed one shows All.
  const only = (f: Pick<Search, "owner" | "status" | "kind">) => {
    fromList.current = false;
    go({ ...f, q: search.q, selected: search.selected }, true);
  };
  const chipOwners = useMemo(() => {
    const ids = new Set(Object.keys(counts?.teams ?? {}));
    if (owner?.startsWith("team:")) ids.add(owner.slice(5));
    return [...ids]
      .map((id) => ({
        id,
        name: teamById.get(id)?.name ?? t("auto.aTeam"),
        team: teamById.get(id),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [counts?.teams, owner, teamById]);

  const newButton = (label: ReactNode) => (
    <button type="button" className="btn btn-primary" onClick={() => open("new")}>
      <Icon name="i-plus" size="s" />
      {label}
    </button>
  );

  const panelOpen = Boolean(selected);
  return (
    <main
      className={panelOpen ? "sub main on autos" : "sub main on autos no-panel"}
      aria-label={t("auto.automations")}
    >
      <div className="page">
        <PageHead
          // R7.5: Automations has no list column, so no Back.
          back={false}
          title={t("auto.automations")}
          subtitle={data ? headerLine(data) : undefined}
          actions={
            none ? null : (
              <>
                <span className="hint-inline col-opt">{t("auto.orAskInChat")}</span>
                {/* One flex item: the button's gap would double the space before "automation". */}
                {newButton(
                  <span>
                    {t("auto.newShort")}
                    <span className="hide-xs">{t("auto.newRest")}</span>
                  </span>,
                )}
              </>
            )
          }
        />
        <div className="toolbar">
          <input
            className="tsearch"
            type="search"
            placeholder={t("auto.searchPlaceholder")}
            aria-label={t("auto.searchLabel")}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {channel && (
            // From a channel's "N automations" pill: those posting there; pressing it shows All.
            <span className="chip-group" role="group" aria-label={t("auto.postsTo")}>
              {/* Pressing it clears it, as its × says (R10-K8). */}
              <Chip pressed clear n={items.length} onClick={() => only({})}>
                {t("auto.postsToChannel", { channel: channelLabel })}
              </Chip>
            </span>
          )}
          <span className="chip-group" role="group" aria-label={t("auto.kind")}>
            <Chip
              pressed={!owner && !status && !kind && !channel && !view}
              n={counts?.all}
              onClick={() => only({})}
            >
              {/* Not "All 0" beside "Reminders 2" (R10-K8). */}
              {t("auto.automations")}
            </Chip>
            <Chip
              pressed={kind === "schedule"}
              n={counts?.timed}
              onClick={() => only(kind === "schedule" ? {} : { kind: "schedule" })}
            >
              <Icon name="i-calendar" size="s" />
              {t("auto.timed")}
            </Chip>
            <Chip
              pressed={kind === "event"}
              n={counts?.triggered}
              onClick={() => only(kind === "event" ? {} : { kind: "event" })}
            >
              <Icon name="i-zap" size="s" />
              {t("auto.triggered")}
            </Chip>
            <Chip
              pressed={view === "reminders"}
              n={reminders.data?.length}
              onClick={() => {
                fromList.current = false;
                go(view === "reminders" ? { q } : { view: "reminders" }, true);
              }}
            >
              <Icon name="i-clock" size="s" />
              {t("auto.reminders")}
            </Chip>
          </span>
          <span className="tsep hide-xs" />
          <span className="chip-group" role="group" aria-label={t("auto.belongsTo")}>
            <Chip
              pressed={owner === "company"}
              onClick={() => only(owner === "company" ? {} : { owner: "company" })}
            >
              <TeamIcon size="s" />
              {t("auto.company")}
            </Chip>
            {chipOwners.map((t) => {
              const value = `team:${t.id}`;
              return (
                <Chip
                  key={t.id}
                  pressed={owner === value}
                  onClick={() => only(owner === value ? {} : { owner: value })}
                >
                  <TeamIcon team={t.team ?? { hue_token: "team-all" }} size="s" />
                  {t.name}
                </Chip>
              );
            })}
            <Chip
              pressed={owner === "mine"}
              onClick={() => only(owner === "mine" ? {} : { owner: "mine" })}
            >
              {t("auto.mine")}
            </Chip>
          </span>
          {((counts?.needs_attention ?? 0) > 0 || status === "needs_attention") && (
            <>
              <span className="tsep hide-xs" />
              <Chip
                danger
                pressed={status === "needs_attention"}
                n={counts?.needs_attention}
                onClick={() =>
                  only(status === "needs_attention" ? {} : { status: "needs_attention" })
                }
              >
                <Icon name="i-alert" size="s" />
                {t("auto.statusNeedsAttention")}
              </Chip>
            </>
          )}
        </div>
        <div className="page-body scroll" data-testid="automations-list">
          {view === "reminders" ? (
            <RemindersList />
          ) : list.isLoading ? (
            <p className="muted">{t("common.loading")}</p>
          ) : list.isError && !data ? (
            <div className="empty" role="alert">
              <h2>{t("auto.listDidntLoad")}</h2>
              <p>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => void list.refetch()}
                >
                  {t("common.retry")}
                </button>
              </p>
            </div>
          ) : none ? (
            <div className="empty auto-empty">
              <span className="auto-ic">
                <Icon name="i-zap" />
              </span>
              <h2>{t("auto.noneTitle")}</h2>
              <p>{t("auto.noneBody")}</p>
              <p>{newButton(t("auto.newAutomation"))}</p>
              <p className="sm">{t("auto.noneAsk")}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="empty">
              <h2>{t("auto.nothingMatches")}</h2>
              <p>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setText("");
                    urlQ.current = "";
                    go({ selected }, true);
                  }}
                >
                  {t("auto.showAll")}
                </button>
              </p>
            </div>
          ) : (
            <div className="dtable">
              <div className="tbl-scroll scroll">
                <table>
                  <thead>
                    <tr>
                      <th>{t("auto.automation")}</th>
                      <th>{t("auto.when")}</th>
                      <th className="col-opt">{t("auto.postsTo")}</th>
                      <th className="hide-sm">{t("auto.belongsTo")}</th>
                      <th>{t("auto.status")}</th>
                      <th className="col-opt">{t("auto.lastRun")}</th>
                      <th className="col-opt">{t("auto.next")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((a) => (
                      <Row
                        key={a.id}
                        a={a}
                        me={me}
                        teams={teamById}
                        selected={a.id === selected}
                        onOpen={() => open(a.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      {selected === "new" ? (
        <NewAutomation
          me={me}
          open={narrow}
          onClose={close}
          onCreated={(id) => {
            // The new one replaces the form's entry: closing still goes Back to the list.
            go({ ...search, selected: id }, true);
          }}
        />
      ) : selected ? (
        <AutomationPanel key={selected} id={selected} me={me} open={narrow} onClose={close} />
      ) : null}
      {panelOpen && narrow && <div className="scrim on" aria-hidden="true" onClick={close} />}
    </main>
  );
}

function Chip({
  pressed,
  n,
  danger = false,
  clear = false,
  onClick,
  children,
}: {
  pressed: boolean;
  n?: number;
  danger?: boolean;
  /** It's a filter in force: an × at its end. */
  clear?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={danger ? "fchipx danger" : "fchipx"}
      aria-pressed={pressed}
      onClick={onClick}
    >
      {children}
      {/* "All 6": the space keeps the count apart in the chip's name too. */}
      {n !== undefined && (
        <>
          {" "}
          <span className="n">{n}</span>
        </>
      )}
      {clear && <Icon name="i-x" size="s" />}
    </button>
  );
}

function Row({
  a,
  me,
  teams,
  selected,
  onOpen,
}: {
  a: Automation;
  me: Me;
  teams: Map<string, TeamSummary>;
  selected: boolean;
  onOpen: () => void;
}) {
  const quiet = a.next_label === "—" || a.status !== "active";
  return (
    <tr
      data-testid="automation-row"
      data-open-panel
      className={selected ? "sel" : undefined}
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      aria-label={t("auto.openDetails", { name: a.name })}
      onClick={onOpen}
      onKeyDown={(e: KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <td>
        <div className="auto-name">
          <KindIcon a={a} />
          <div className="auto-name-t">
            <div className="cell-main">{a.name}</div>
            <div className="cell-sub">{sublineOf(a)}</div>
          </div>
        </div>
      </td>
      <td className="when" title={a.when_label}>
        {whenShort(a.when_label)}
        {/* Where the Next column is hidden (iPad portrait), it's said under When (R10-K8). */}
        {!quiet && <span className="when-next">{t("auto.nextWhen", { when: a.next_label })}</span>}
      </td>
      <td className="col-opt">{postsTo(a.destination, me.id)}</td>
      <td className="hide-sm">
        <OwnerChip owner={a.owner} teams={teams} />
      </td>
      <td>
        <StatusPill a={a} />
      </td>
      <td className="col-opt">{lastRunLabel(a.last_run_at)}</td>
      <td className={quiet ? "col-opt muted" : "col-opt"}>{a.next_label}</td>
    </tr>
  );
}
